/* istanbul ignore file */
import { DATE_FORMAT, useDateService, EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { FieldProps, useFieldHelper } from 'framework/ui/field';
import { Badge } from 'framework/ui/mui-builder';
import {
  getBadgeColor,
  isStatusAllowed,
  getValueWithUnit,
  DEFAULT_TIME_UNITS,
  type DaysBorderForColor,
} from 'domain/shared/core/lastTouchHelpers';

interface LastTouchCrmFieldProps extends FieldProps {
  statusSource: string;
  colorBorders: DaysBorderForColor;
  skipStates: string[];
  exceedDaysForStale?: number;
}

const LastTouchCrmField = ({
  source,
  statusSource,
  colorBorders,
  skipStates,
  exceedDaysForStale,
  ...props
}: LastTouchCrmFieldProps) => {
  const { getValue: getLastModified } = useFieldHelper({ source });
  const { getValue: getStatus } = useFieldHelper({ source: statusSource });
  const { getHoursBetween, getMomentDateObjAsUtc, getCurrentDateTimeInUtc } = useDateService();

  const status = getStatus();
  const isNeedCalculate = isStatusAllowed(status, skipStates);

  const lastModified = getMomentDateObjAsUtc(getLastModified());
  const currentDateTime = getCurrentDateTimeInUtc();
  const hoursBetween = getHoursBetween(currentDateTime, lastModified, DATE_FORMAT.FULL);
  const roundHoursValue = isNeedCalculate ? Math.round(hoursBetween) : -1;
  const days = isNeedCalculate ? roundHoursValue / 24 : -1;
  const color = getBadgeColor(days, colorBorders);

  let resultValue;

  if (days === -1) {
    resultValue = EMPTY_VALUE_INDICATOR;
  } else if (days >= 0 && days < 1) {
    resultValue = getValueWithUnit(roundHoursValue, DEFAULT_TIME_UNITS.HOUR);
  } else {
    const roundDays = Math.round(days);
    resultValue =
      exceedDaysForStale && roundDays > exceedDaysForStale
        ? 'STALE'
        : getValueWithUnit(roundDays, DEFAULT_TIME_UNITS.DAY);
  }

  return (
    <Badge
      color={color}
      sx={{
        ml: 1,
        height: '2em',
        width: 70,
        '&:first-child': {
          ml: 0,
        },
      }}
      {...props}
    >
      {resultValue}
    </Badge>
  );
};

export default LastTouchCrmField;
